"use client"

import { HEADER_SLOGAN } from "@/constants/constant"
import { Images } from "@/constants/images"
import { useTamdaMediaSettings } from "@/hook/useTamdaMediaSettings"
import { t } from "@/languages/i18n"
import { HeaderDetailProps } from "@/types/types"
import Image from "next/image"
import Link from "next/link"
import React, { useState } from "react"

const HeaderDetail: React.FC<HeaderDetailProps> = ({ data, className = "", onSearchClick }) => {
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const { settings } = useTamdaMediaSettings()

  const displayPhone = settings?.phone || data.contactInfo.phone
  const displayFacebookUrl = settings?.facebook || data.socialLinks?.facebook
  const displayYoutubeUrl = settings?.youtube || data.socialLinks?.youtube

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      window.location.href = `/search?q=${encodeURIComponent(searchQuery)}`
    }
  }

  return (
    <div className={`w-full bg-white ${className}`}>
      <div className="max-w-[1128px] max-h-[126px] mx-auto shadow-lg lg:shadow-none">
        {/* Desktop Layout */}
        <div className="hidden sm:flex items-start justify-between">
          {/* Left: Date and Currency (vertical) */}
          <div className="flex flex-col gap-[1.875rem] text-sm text-gray-600 pt-[1.875rem] pb-[1.4375rem] w-[25%]">
            <span className="font-bold text-[#333333] text-[0.875rem] leading-[1.35625rem]">{data.currentDate}</span>
            <span className="font-medium text-[#333333] text-[0.875rem] leading-[1.35625rem]">
              1 EUR = {Number(data.currencyRate).toFixed(2)} CZK
            </span>
          </div>

          <div className="flex flex-col items-center gap-[1.1875rem] pt-[1.375rem] pb-[1.4375rem] w-[45.84%]">
            <Link
              href="/"
              className="flex-shrink-0 relative w-[232px] h-[40px] transition-all duration-300 hover:brightness-110"
            >
              <Image src={Images.logoTamdaMedia} alt={t("TamdaMedia Logo")} fill className="h-auto w-auto" />
            </Link>
            <p className="text-[#333333] text-[0.875rem] font-bold leading-[21.7px] text-center uppercase">
              {t(HEADER_SLOGAN)}
            </p>
          </div>

          <div className="flex flex-col pt-[1.625rem] pb-[13px] w-[29.16%] items-end">
            <div className="w-[240px] flex flex-col gap-7">
              <div className="flex items-center gap-6 justify-between">
                {displayPhone && (
                  <Link
                    href={`tel:${displayPhone}`}
                    className="flex items-start gap-2 text-gray-700 hover:opacity-80 transition-opacity"
                  >
                    <Image
                      src={Images.phoneIcon}
                      alt={t("Phone")}
                      width={20}
                      height={20}
                      className="w-[1.5625rem] h-6"
                    />
                    <span className="text-sm font-medium leading-[21.7px] ">{displayPhone}</span>
                  </Link>
                )}

                <div className="flex items-center gap-1">
                  {displayFacebookUrl && (
                    <Link
                      href={displayFacebookUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-[24px] h-[24px] flex items-center justify-center bg-transparent rounded-full hover:opacity-80 transition-opacity"
                      aria-label={t("Facebook Link")}
                    >
                      <svg viewBox="0 0 64 64" width="24" height="24">
                        <circle cx="32" cy="32" r="31" fill="#3b5998"></circle>
                        <path
                          d="M34.1,47V33.3h4.6l0.7-5.3h-5.3v-3.4c0-1.5,0.4-2.6,2.6-2.6l2.8,0v-4.8c-0.5-0.1-2.2-0.2-4.1-0.2 c-4.1,0-6.9,2.5-6.9,7V28H24v5.3h4.6V47H34.1z"
                          fill="white"
                        ></path>
                      </svg>
                    </Link>
                  )}
                  {displayYoutubeUrl && (
                    <Link
                      href={displayYoutubeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-[23.25px] h-[23.25px] flex items-center justify-center bg-transparent rounded-full hover:opacity-80 transition-opacity"
                      aria-label={t("Youtube Link")}
                    >
                      <Image
                        src={Images.youtubeIconHeader}
                        alt={t("Youtube Link")}
                        width={23.25}
                        height={23.25}
                        className="w-[23.25px] h-[23.25px]"
                      />
                    </Link>
                  )}
                </div>
              </div>

              <form onSubmit={handleSearch} className="relative">
                <div className="relative bg-[#F5F5F5] h-[36px] rounded-[0.3125rem] flex items-center">
                  <input
                    type="text"
                    placeholder={t("Search")}
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="w-[180px] mx-3 pr-10 pb-[0.25px] placeholder:text-[#242F3E] placeholder:text-opacity-90 text-sm bg-transparent focus:outline-none focus:border-[#0C9443] transition-colors"
                  />
                  <button
                    type="submit"
                    className="absolute right-2 top-1/2 -translate-y-1/2 pb-[2.5px] hover:opacity-70 transition-opacity"
                    aria-label={t("Search")}
                  >
                    <Image
                      src={Images.tamdaSearchIcon}
                      alt={t("Search")}
                      width={20}
                      height={20}
                      className="w-[1.1875rem] h-[1.1875rem]"
                    />
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Mobile Layout - Only Logo */}
        <div className="sm:hidden flex items-center justify-center py-5">
          <Link
            href="/"
            className="flex-shrink-0 relative w-[200px] h-[35px] transition-all duration-300 hover:brightness-110"
          >
            <Image
              src={Images.logoTamdaMedia}
              alt={t("TamdaMedia Logo")}
              fill
              className="h-auto w-auto object-contain"
            />
          </Link>
        </div>
      </div>
    </div>
  )
}

export default HeaderDetail
